/*
 * Paste (A28, the composer half). The clipboard's plain text is what goes in tonight; rich text
 * through `turndown` is S2, and `pasteToMarkdown` is the one function that changes then. A URL
 * pasted over a selection turns the selection into a link (spec 12 §4.4). The whole paste is one
 * browser-undo step because it goes through `applyEdit`.
 *
 * A paste too long to read in the box — a log, a stack trace — becomes a text file in the tray
 * instead, where a surface asked for that (`ComposerProps.longPaste`); `pastedTextFile` makes it.
 */

import { formatNumber, t } from "../../i18n/index.ts"
import type { LongPaste } from "../types.ts"
import type { Edit } from "./edit.ts"

const URL = /^https?:\/\/\S+$/i

/** The markdown a paste inserts, or null to let the browser handle the event (a file, say). */
export function pasteToMarkdown(data: DataTransfer): string | null {
	const text = data.getData("text/plain")
	return text === "" ? null : text
}

export function pasteEdit(text: string, start: number, end: number, pasted: string): Edit {
	const url = pasted.trim()
	if (start !== end && URL.test(url)) {
		const insert = `[${text.slice(start, end)}](${url})`
		const at = start + insert.length
		return { text: text.slice(0, start) + insert + text.slice(end), start: at, end: at }
	}
	const at = start + pasted.length
	return { text: text.slice(0, start) + pasted + text.slice(end), start: at, end: at }
}

/** A paste file's name: `paste-1.txt`, and the next number while earlier ones are in the tray. */
const PASTE_NAME = /^paste-(\d+)\.txt$/

/**
 * The text file a long paste becomes, and the sentence its chip carries: "Pasted text · 1,240
 * lines", plus whatever the surface's `transform` said it did ("1 secret hidden"). The number in the
 * name is one above the highest paste already in the tray, so two pastes never share a name and a
 * removed one leaves no confusing gap-filler behind.
 */
export function pastedTextFile(
	text: string,
	trayNames: readonly string[],
	transform?: LongPaste["transform"],
): { file: File; note: string } {
	const changed = transform?.(text)
	const body = changed?.text ?? text
	const highest = trayNames.reduce((max, name) => {
		const n = Number(PASTE_NAME.exec(name)?.[1] ?? 0)
		return Math.max(max, n)
	}, 0)
	const lines = body === "" ? 0 : body.split("\n").length
	const note = [
		// The key is chosen here rather than through a `count`, so the number can be printed as the
		// interface prints numbers ("1,240").
		t(lines === 1 ? "composer.attach.pasted.one" : "composer.attach.pasted", {
			lines: formatNumber(lines),
		}),
		...(changed?.note === undefined || changed.note === "" ? [] : [changed.note]),
	].join(" · ")
	return {
		file: new File([body], `paste-${highest + 1}.txt`, { type: "text/plain" }),
		note,
	}
}
